import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { QtiRenderer } from '@/components/qti/QtiRenderer';
import { QtiResponse, QtiResponses } from '@/components/qti/types';
import { qtiApi } from '@/lib/http.server';
import { handleServerFnError } from '@/lib/sentry-helpers';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserAuthenticated } from '@/middlewares/userAuthMiddleware';

export const Route = createFileRoute('/qti-embed/$qtiId')({
  component: RouteComponent,
  // validateSearch: (search: Record<string, unknown>) => {
  //   return {
  //     primary_color: (search.primary_color as string) ?? '',
  //   };
  // },
  loader: async ({ params }) => {
    const { qtiId } = params;
    const qtiItem = await fetchQtiItem({ data: { qtiId } });
    return { xml: qtiItem.rawXml };
  },
});

const fetchQtiItem = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      qtiId: z.string(),
    })
  )
  .handler(async ({ context: { cognitoUser }, data: { qtiId } }) => {
    assertUserAuthenticated(cognitoUser);
    try {
      const { data } = await qtiApi.get(`/assessment-items/${qtiId}`);
      return data;
    } catch (error) {
      return handleServerFnError(error, {
        service: 'qti-api',
        function: 'fetchQtiItem',
        endpoint: `/assessment-items/${qtiId}`,
      });
    }
  });

function RouteComponent() {
  const { xml } = Route.useLoaderData();
  const [responses, setResponses] = useState<QtiResponses>({});
  const [displayResponseFeedback, setDisplayResponseFeedback] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Process messages from parent
      if (event.data && typeof event.data === 'object') {
        if (event.data.type === 'QTI_SET_RESPONSE') {
          const { responseIdentifier, response } = event.data;
          handleResponseChange(responseIdentifier, response);
        }

        if (event.data.type === 'QTI_DISPLAY_FEEDBACK') {
          const { displayResponseFeedback } = event.data;
          setDisplayResponseFeedback(displayResponseFeedback);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleResponseChange = (
    responseIdentifier: string,
    response: QtiResponse
  ) => {
    setResponses((prev) => ({
      ...prev,
      [responseIdentifier]: response,
    }));

    window.parent.postMessage(
      {
        type: 'QTI_RESPONSE_CHANGE',
        responseIdentifier,
        response,
      },
      '*'
    );
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <QtiRenderer
        xml={xml}
        responses={responses}
        displayResponseFeedback={displayResponseFeedback}
        onResponseChange={handleResponseChange}
      />
    </div>
  );
}
